import { useTranslation } from 'next-i18next'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import AirlineCompatibility from '@src/quince-components/src/atoms/airlineCompability/AirlineCompability'
import { AIRLINE_COMPATIBILITY_HASH } from '@src/quince-components/src/atoms/productForm/product-form-shared'
import useHash from '@src/quince-components/src/hooks/useHash'
import { ProductDetailsTabWithReadMore } from '@src/quince-site-pdp/src/organisms/productPageSections/product-details-tabs'
import styles from '@src/quince-site-pdp/src/organisms/productPageSections/productDetailsAndCare/productDetailsAndCare.module.scss'

import { toAccordionItems, toAirlineCompatibilitySection, toDetailsTabTracker } from './accordion.adapter'

import type { AccordionItemsProp } from './accordion.adapter'
import type { AccordionWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export function AccordionWidgetComponentLegacy(props: PLTypedWidgetProps<AccordionWidgetData> & AccordionItemsProp) {
  // Rows are resolved by the widget shell (`index.tsx`) so the HTML-children vs SVD-attribute choice is made
  // once; fall back to flattening the children here for any direct render. `Widget.widgets` is a recursive
  // proto type that can't be read cleanly through `Omit<Widget,'data'>`, so read the nested HTML children via
  // a record cast (mirrors the FAQ widget); the adapter narrows them.
  const propsRecord = props as unknown as Record<string, unknown>
  const items = props.items ?? toAccordionItems(propsRecord.widgets)
  const { t } = useTranslation('common')

  // Airline compatibility rides on the ACCORDION's own data; the DETAILS `#airline-compatibility` link
  // opens the legacy modal via the hash (below).
  const airlineSection = toAirlineCompatibilitySection(props.data.accordionData)

  const [hash] = useHash()
  const showAirlineCompatibility = hash === AIRLINE_COMPATIBILITY_HASH

  // Clear the hash so the modal can be reopened — mirrors the non-PL close in
  // `use-product-form.tsx`'s `hideAirlineCompatbility` byte-for-byte. Deliberately not using
  // `router.replace`/`router.push` from the next router: `router.replace(pathname)` triggers a
  // route transition (scroll reset + re-render), and `router.push({ hash: '' })` still pushes a
  // trailing `#` into history. `history.replaceState` + a manual `hashchange` dispatch is a
  // scoped no-navigation update that just resets the hash and notifies `useHash` — matching
  // the non-PL behavior exactly.
  const hideAirlineCompatibility = () => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
    // eslint-disable-next-line i18next/no-literal-string
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  if (items.length === 0) return null

  return (
    <>
      <div className={styles.productDetailsAndCareTabs__tabWrapper}>
        {items.map((item, index) => (
          <ProductDetailsTabWithReadMore
            key={item.id}
            tabId={item.id}
            // A real per-row tracker so ProductDetailsAndCareV2 fires `click__tabs__tab` on expand — it
            // no-ops on an empty tracker (the prior `tracker: ''`). No `trackingMetaData` is passed: non-PL
            // fires this click with NO container context, so PL must not add any (payload parity).
            tab={{
              id: item.id,
              title: item.title,
              content: item.html,
              // SVD rows carry a pre-resolved tracker (their tab type is known from the attribute key, so no
              // locale-dependent title lookup is needed); HTML-child rows resolve it as before.
              tracker: item.tracker ?? toDetailsTabTracker(item.title, item.id),
            }}
            textContent={item.html}
            isFirstTab={index === 0}
            // Match non-PL EXACTLY: on this PDP only the "More Information" row is open by default — the
            // first row (DETAILS) stays COLLAPSED (verified in the browser on non-PL). So force ONLY the
            // More-Information row open (title match, case-insensitive) and return an explicit `false` for
            // every other row. `false` (not `undefined`) is deliberate: `undefined` would let
            // `ProductDetailsAndCareV2` fall back to its `isFirstTab` default and open the first row too
            // ("all rows open" regression); `false` keeps it closed. We also IGNORE proto `defaultExpanded`
            // (the BE sends `true` on other rows, which would over-open). STRFRNT-15816.
            initiallyOpen={item.title.trim().toLowerCase() === t('more_information').trim().toLowerCase()}
            // Suppresses the first row's top border on MOBILE — the shared row only draws it when
            // `!isReviewHighlightsSectionDisplayed || !isMobile`. On mweb we don't want that divider;
            // desktop keeps it via the component's `!isMobile` branch.
            isReviewHighlightsSectionDisplayed={true}
          />
        ))}
      </div>
      {airlineSection && (
        <AirlineCompatibility
          show={showAirlineCompatibility}
          onCloseAirlineCompatbility={hideAirlineCompatibility}
          airlineCompatibilityListingCS={[airlineSection]}
        />
      )}
    </>
  )
}
